import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Chip, Divider, Grid, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import Loader from "@jumbo/components/Loader";
import { getPrebookOrderTracking } from "app/redux/slice/prebookOrderSlice";

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, "0");
  const h24 = d.getHours();
  const h12 = h24 % 12 || 12;
  return `${pad(d.getDate())}/${pad(
    d.getMonth() + 1
  )}/${d.getFullYear()}, ${h12}:${pad(d.getMinutes())} ${h24 >= 12 ? "PM" : "AM"}`;
};

// Stages the order can pass through, in order. Anything the backend hasn't
// reported yet is rendered as a hollow "not reached" step so the whole journey
// is visible, not just what has happened.
// Payment sits before dispatch: the parcel goes out once the money is in.
const ALL_STAGES = [
  "Order Created",
  "Pending Approval",
  "Approved",
  "PO Created",
  "Invoice Created",
  "Payment Received",
  "Dispatched",
];

const Detail = ({ label, value }) => (
  <Grid item xs={6} sm={3}>
    <Typography variant="body2" color="text.secondary" mb={0}>
      {label}
    </Typography>
    <Typography variant="body1" mb={0}>
      {value ?? "-"}
    </Typography>
  </Grid>
);

const OrderTracking = ({ preOrderId }) => {
  const dispatch = useDispatch();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    dispatch(getPrebookOrderTracking(preOrderId))
      .unwrap()
      .then((response) => {
        if (!active) return;
        if (response.status === 200) {
          setTracking(response.data?.data?.tracking ?? null);
        }
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [dispatch, preOrderId]);

  if (loading) return <Loader />;

  if (!tracking) {
    return (
      <Typography variant="body1" sx={{ py: 3, textAlign: "center" }}>
        Tracking information is not available for this order.
      </Typography>
    );
  }

  const reached = {};
  (tracking.timeline || []).forEach((stage) => {
    reached[stage.stage] = stage;
  });
  const isRejected = tracking.preOrder?.approvalStatus === "Rejected";

  // A rejected order stops there - the later stages can never happen.
  const stages = isRejected
    ? ["Order Created", "Pending Approval", "Rejected"]
    : ALL_STAGES;

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Detail label="Pre-Order" value={tracking.preOrder?.preOrderNumber} />
        <Detail label="PO" value={tracking.purchaseOrder?.poNumber} />
        <Detail label="Invoice" value={tracking.invoice?.invoiceNumber} />
        <Detail label="Vendor" value={tracking.preOrder?.supplier?.name} />
      </Grid>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <Chip
          size="small"
          label={`Status: ${tracking.currentStatus}`}
          color={isRejected ? "error" : "primary"}
        />
        <Chip
          size="small"
          label={`Payment: ${tracking.paymentStatus || "—"}`}
          color={tracking.paymentStatus === "paid" ? "success" : "default"}
        />
        <Chip
          size="small"
          label={`Dispatch: ${tracking.dispatchStatus || "—"}`}
          color={
            tracking.dispatchStatus === "dispatched" ? "success" : "default"
          }
        />
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Stack spacing={0}>
        {stages.map((name, index) => {
          const hit = reached[name];
          const done = Boolean(hit);
          const isLast = index === stages.length - 1;
          const rejectedStep = name === "Rejected";

          return (
            <Stack key={name} direction="row" spacing={2}>
              <Stack alignItems="center">
                {done ? (
                  rejectedStep ? (
                    <CancelIcon color="error" />
                  ) : (
                    <CheckCircleIcon color="success" />
                  )
                ) : (
                  <RadioButtonUncheckedIcon color="disabled" />
                )}
                {!isLast && (
                  <Box
                    sx={{
                      width: 2,
                      flex: 1,
                      minHeight: 26,
                      bgcolor: done ? "success.light" : "divider",
                    }}
                  />
                )}
              </Stack>
              <Box sx={{ pb: isLast ? 0 : 2 }}>
                <Typography
                  variant="body1"
                  mb={0}
                  sx={{
                    fontWeight: done ? 600 : 400,
                    color: done ? "text.primary" : "text.disabled",
                  }}
                >
                  {name}
                  {hit?.poNumber ? ` · ${hit.poNumber}` : ""}
                  {hit?.invoiceNumber ? ` · ${hit.invoiceNumber}` : ""}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0}>
                  {done ? formatDateTime(hit.at) : "Pending"}
                </Typography>
                {hit?.reason && (
                  <Typography variant="body2" color="error.main" mb={0}>
                    Reason: {hit.reason}
                  </Typography>
                )}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

export default OrderTracking;
